"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, Calendar, ShieldCheck, ArrowRight, Clock, UserCheck, Sparkles, Loader as Loader2, Lock } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
function ConsultationFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 text-center space-y-4 rounded-xl bg-card border border-border shadow-md">
        <div className="w-14 h-14 bg-accent/20 text-accent rounded-full flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <h3 className="text-xl font-semibold text-foreground">Consultation Request Received!</h3>
        <p className="text-muted-foreground text-sm max-w-md mx-auto">
          {successMessage || "Thank you! I personally review every enquiry and will get back to you within 24 business hours with next steps."}
        </p>
        <div className="pt-2">
          <Button onClick={resetForm} variant="outline" className="text-sm font-medium">
            Send Another Enquiry
          </Button>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="fullName" rules={{
      required: "Please enter your full name",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium text-xs uppercase tracking-wider">
              Full Name *
            </FormLabel>
            <FormControl>
              <Input placeholder="e.g. John Smith" className="bg-background border-input text-foreground h-11 px-3" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="email" rules={{
        required: "Please enter your email",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium text-xs uppercase tracking-wider">
                Work Email *
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@company.com" className="bg-background border-input text-foreground h-11 px-3" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="websiteUrl" rules={{
        required: "Please enter your website URL"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium text-xs uppercase tracking-wider">
                Website URL *
              </FormLabel>
              <FormControl>
                <Input type="text" placeholder="yourcompany.com" className="bg-background border-input text-foreground h-11 px-3" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="goals" rules={{
      required: "Please share a few details about your primary objective"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium text-xs uppercase tracking-wider">
              Primary Goal or SEO Challenge *
            </FormLabel>
            <FormControl>
              <Input placeholder="e.g. Increase organic lead volume, fix technical issues" className="bg-background border-input text-foreground h-11 px-3" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} size="lg" className="w-full bg-accent text-accent-foreground hover:bg-accent/90 h-12 text-base font-semibold transition-colors shadow-md group mt-2">
        {isSubmitting ? <>
            <Loader2 className="animate-spin size-5 mr-2" />
            Booking Your Consultation...
          </> : <>
            Book a Free SEO Consultation
            <ArrowRight className="size-5 ml-1 transition-transform group-hover:translate-x-1" />
          </>}
      </Button>

      <p className="text-center text-xs text-muted-foreground flex items-center justify-center gap-1.5 pt-1">
        <Lock className="size-3 text-muted-foreground" />
        {"100% confidential. No obligations & no high-pressure sales."}
      </p>
    </Form>;
}
export default function ConsultationCTA() {
  const shouldReduceMotion = useReducedMotion();
  const benefits = [{
    title: "Direct Access to Yassine",
    description: "You work directly with me—an independent consultant—not an account manager or junior worker."
  }, {
    title: "Customized Growth Roadmap",
    description: "An initial high-level teardown of your keyword potential and main technical barriers."
  }, {
    title: "Zero Sales Gimmicks",
    description: "Honest, practical feedback on what will actually move the needle for your business search strategy."
  }];
  const highlights = [{
    icon: Clock,
    text: "30-Min Video Call"
  }, {
    icon: UserCheck,
    text: "1-on-1 with Yassine"
  }, {
    icon: ShieldCheck,
    text: "No Long Contracts"
  }];
  return <section id="consultationcta" data-nav="light" className="py-20 md:py-28 bg-background relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -mr-20 -mt-20" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl -ml-20 -mb-20" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-6 space-y-6">
            <Badge variant="outline" className="px-3 py-1 text-xs border-primary/30 text-primary bg-primary/5 rounded-full">
              <Sparkles className="size-3.5 mr-1.5 text-accent" />
              Direct Senior Expertise
            </Badge>

            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-serif leading-tight">
              Ready to grow your search traffic with a tailor-made strategy?
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              {"I personally review every website before we talk. You'll work directly with me—Yassine Essaadi—to map out a clear, sustainable path to search authority."}
            </p>

            <div className="space-y-4 pt-2">
              {benefits.map((benefit, i) => <div key={i} data-index={i} className="flex items-start gap-3">
                  <div className="mt-1 rounded-full p-1 bg-primary/10 text-primary shrink-0">
                    <CheckCircle2 className="size-4" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground text-sm">{benefit.title}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{benefit.description}</p>
                  </div>
                </div>)}
            </div>

            <div className="pt-4 flex items-center gap-4 border-t border-border mt-6">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_consultant_avatar.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_consultant_avatar.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_consultant_avatar.webp-wvc-sizes" alt="Yassine Essaadi - Freelance SEO Consultant" className="w-14 h-14 rounded-full object-cover border-2 border-primary/20 shadow-sm shrink-0" loading="lazy" />
              <div>
                <p className="text-sm font-semibold text-foreground">Yassine Essaadi</p>
                <p className="text-xs text-muted-foreground">Lead Freelance SEO Consultant at YE Consultancy</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="inline-block size-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-xs text-muted-foreground font-medium">Currently taking select clients</span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              {highlights.map((item, i) => {
              const IconComponent = item.icon;
              return <div key={i} data-index={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-muted text-foreground text-xs font-medium">
                    <IconComponent className="size-3.5 text-primary" />
                    {item.text}
                  </div>;
            })}
            </div>
          </motion.div>

          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 25
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="lg:col-span-6">
            <Card className="shadow-xl border-border bg-card/95 backdrop-blur-sm rounded-2xl overflow-hidden p-2 sm:p-4">
              <div className="p-4 sm:p-6 pb-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-accent uppercase tracking-wider flex items-center gap-1">
                    <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                    Free 30-Min Session
                  </span>
                  <Badge variant="secondary" className="text-[13px] font-normal">
                    Direct Consultation
                  </Badge>
                </div>
                <h3 className="text-2xl font-bold text-foreground font-serif">
                  Book Your Strategy Call
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  {"Fill in your details below to request a direct 1-on-1 consultation."}
                </p>
              </div>

              <CardContent className="pt-2 sm:pt-4">
                <FormProvider formId="consultation-cta-form" sectionName="consultation-cta" defaultValues={{
                fullName: "",
                email: "",
                websiteUrl: "",
                goals: ""
              }} validationRules={{
                fullName: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                websiteUrl: {
                  required: true
                },
                goals: {
                  required: true
                }
              }} submitText="Book a Free SEO Consultation" successMessage="Thank you! Your request has been sent directly to Yassine. Expect a personal reply shortly.">
                  <ConsultationFormContent />
                </FormProvider>

                <div className="mt-6 pt-4 border-t border-border/60 flex items-center justify-between text-xs text-muted-foreground">
                  <span>Need an immediate audit?</span>
                  <Link to="/ContactPage" className="text-primary hover:text-primary/80 font-medium underline underline-offset-4 flex items-center gap-1">
                    Contact page directly
                    <ArrowRight className="size-3" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </motion.div>

        </div>
      </div>
    </section>;
}